import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { getRegisterLanguagesByRegion } from '@innovamat/localization';

import { useUser } from '../../../user-management';
import LoginRegisterLayout from '../../components/login-register-layout/login-register-layout';
import { SuccessMessage } from '../../components/success-message';
import { useCompleteInvitation } from '../../hooks/use-complete-invitation';
import type {
  InvitationType,
  UserInvitation,
} from '../../hooks/use-complete-invitation/invitation';
import { useResendRegisterInvitation } from '../../hooks/use-resend-register-invitation';
import { useGetUserRegion } from '../../hooks/use-user-region';
import { CompleteInvitationForm } from './complete-invitation-form';
import ModalError from './modal-error';
import { isValidInvitationLink } from './model/invitation/invitation-link';

const getUserType = (path: string, invitationType: InvitationType) => {
  const type = path.includes('complete-parent-invitation')
    ? 'parent'
    : invitationType;

  return {
    type,
    isParent: type === 'parent',
    isStudent: type === 'student',
    isTeacher: type === 'teacher',
  };
};

const ERROR_TYPES = {
  USER_EXISTS: 'userExists',
  EXPIRED_LINK: 'expiredLink',
  GENERIC: 'generic',
} as const;

type ErrorType = (typeof ERROR_TYPES)[keyof typeof ERROR_TYPES];

export function CompleteInvitation(): JSX.Element {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const { isLoggedIn, onClearUserSession } = useUser();
  const [showErrorModal, setShowErrorModal] = useState<ErrorType | undefined>(
    undefined
  );

  const { resendRegisterInvitation, isPendingResendInvitation } =
    useResendRegisterInvitation();

  const invitationId = params.get('id') || undefined;
  const invitationToken = params.get('token') || undefined;
  const email = params.get('email') || undefined;
  const invitationType = params.get('type') || undefined;
  const region = useGetUserRegion();
  const languages = getRegisterLanguagesByRegion(region);

  const isValidLink = isValidInvitationLink({
    email: email,
    id: invitationId,
    region: region,
    token: invitationToken,
    type: invitationType,
  });

  const { type, isParent, isStudent, isTeacher } = getUserType(
    location.pathname,
    invitationType as InvitationType
  );

  const { error, isError, isPending, isSuccess, mutate, reset } =
    useCompleteInvitation({
      type,
    });

  const getErrorType = (): ErrorType => {
    if (error?.response?.status === 409) {
      return ERROR_TYPES.USER_EXISTS;
    }

    if (error?.response?.status === 404 || !isValidLink) {
      return ERROR_TYPES.EXPIRED_LINK;
    }

    return ERROR_TYPES.GENERIC;
  };

  const getLanguage = (): string => {
    const urlLang = params.get('language');

    if (urlLang && languages.includes(urlLang)) {
      return urlLang;
    }

    if (languages.includes(i18n.language)) {
      return i18n.language;
    }

    return languages[0];
  };

  const language = getLanguage();

  const userInvitationDefaultData: UserInvitation = {
    firstName: '',
    lastName: '',
    email,
    phoneNumber: null,
    password: '',
    invitationToken,
    invitationId,
    language: language,
  };

  const goToLogin = (): void => {
    navigate('/');
  };

  const goToForgotPassword = (): void => {
    navigate(`/${i18n.language}/forgot-password`);
  };

  const handleResendLink = (): void => {
    if (!invitationId || !invitationToken) return;

    resendRegisterInvitation({
      id: invitationId,
      token: invitationToken,
      type,
    });

    setShowErrorModal(undefined);
  };

  const handleFormSubmit = (data: UserInvitation): void => {
    const body = { ...data };

    if (!isParent) {
      body.parentAcceptCommunication = undefined;

      if (!body.phoneNumber || !isValidPhoneNumber(body.phoneNumber)) {
        body.phoneNumber = null;
      }
    }

    mutate(body);
  };

  const closeErrorModal = (): void => {
    setShowErrorModal(undefined);
    reset();
  };

  if (isError) {
    setShowErrorModal(getErrorType());
    reset();
  }

  useEffect(() => {
    if (isLoggedIn) {
      onClearUserSession();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language, i18n]);

  return (
    <>
      <LoginRegisterLayout
        isRegisterLayout
        isSuccess={isSuccess}
        title={t('teacher.onboarding.createProfileFilled.title')}
        description={t(
          'teacher.onboarding.createProfileFilled.subtitle.personalData'
        )}
        error={
          showErrorModal === ERROR_TYPES.GENERIC ? (
            <>{t('teacher.onboarding.createProfileFilled.error')}</>
          ) : undefined
        }
        onSuccessBody={
          <SuccessMessage
            body={t('teacher.onboarding.createProfileFilled.success.body')}
            title={t('teacher.onboarding.createProfileFilled.success.title')}
            actionText={t(
              'teacher.onboarding.createProfileFilled.button.access'
            )}
            action={goToLogin}
            dataTestId="complete-invitation-success"
          />
        }
      >
        <CompleteInvitationForm
          data={userInvitationDefaultData}
          isPending={isPending}
          languages={languages}
          region={region}
          userType={{ type, isParent, isStudent, isTeacher }}
          onSubmit={handleFormSubmit}
        />
      </LoginRegisterLayout>
      <ModalError
        isOpen={!!showErrorModal}
        email={email}
        goToLogin={goToLogin}
        goToForgotPassword={goToForgotPassword}
        loadingReenvitation={isPendingResendInvitation}
        expiredLinkError={showErrorModal === ERROR_TYPES.EXPIRED_LINK}
        userExistsError={showErrorModal === ERROR_TYPES.USER_EXISTS}
        onClose={closeErrorModal}
        onResendLink={handleResendLink}
      />
    </>
  );
}
